import Dialog from '@mui/material/Dialog';
import { LeavePlanSetupDialog } from '@components/ProductOffers/configuration/modals';
import { useCloseGuards } from '@components/ProductOffers/configuration/ConfigurePlanDialog/useCloseGuards';
import { EquipmentSelectionStep, ServiceAddOnsStep, TVAddOnsStep } from '@components/ProductOffers/configuration/steps';
import { useShallow } from 'zustand/react/shallow';
import { useConfigurationStore } from '@store/configurationStore';
import { useConfigurePlanActions } from '@hooks/useConfigurePlanActions/useConfigurePlanActions';
import { ChannelListSideModal } from '@components/ProductOffers/modals/ChannelListSideModal';
import { WarningDialog } from '@shared/components/WarningDialog/WarningDialog';
import type { PlanCategory } from '@t/configuration.types';
import type { EditModeConfig } from '@t/editMode.types';

interface ConfigurePlanDialogProps {
  editMode?: EditModeConfig | null;
  hasUnsavedChanges?: boolean;
  editLoading?: boolean;
  onSaveComplete?: () => void;
  onCancelEdit?: () => void;
}

const ADD_ONS_STEP_LABELS: Record<PlanCategory, string> = {
  Internet: 'Internet',
  Phone: 'Phone',
  Video: 'TV',
  EV: '',
  Bundle: '',
};

export const ConfigurePlanDialog: React.FC<ConfigurePlanDialogProps> = ({
  editMode,
  hasUnsavedChanges,
  editLoading,
  onSaveComplete,
  onCancelEdit,
}) => {
  const { isOpen, activePlanConfig, currentStep, channelSideModal, closeChannelSideModal } = useConfigurationStore(
    useShallow((s) => ({
      isOpen: s.isPopupOpen,
      activePlanConfig: s.activePlanConfig,
      currentStep: s.currentStep,
      channelSideModal: s.channelSideModal,
      closeChannelSideModal: s.closeChannelSideModal,
    }))
  );
  const { confirmHardware, addSelectedAddOnsToPlan, skipAddOns, isPending } = useConfigurePlanActions();

  const planCategory = activePlanConfig?.planCategory;
  const isEditing = !!editMode;
  const stepLoading = isEditing ? !!editLoading : isPending;

  const {
    showLeaveDialog,
    showDiscardWarning,
    handleClose,
    handleLeave,
    dismissLeaveDialog,
    discardChanges,
    dismissDiscardWarning,
  } = useCloseGuards({ isEditing, hasUnsavedChanges, onCancelEdit });

  const renderStep = () => {
    if (!planCategory) return null;

    if (currentStep === 'equipment') {
      return (
        <EquipmentSelectionStep
          onClose={handleClose}
          onConfirm={isEditing && onSaveComplete ? onSaveComplete : confirmHardware}
          confirmLabel={isEditing ? 'Save Changes' : undefined}
          loading={stepLoading}
        />
      );
    }

    if (planCategory === 'Video') {
      return (
        <TVAddOnsStep
          onClose={handleClose}
          onSkip={skipAddOns}
          onAddToPlan={isEditing && onSaveComplete ? onSaveComplete : addSelectedAddOnsToPlan}
          confirmLabel={isEditing ? 'Save Changes' : undefined}
          onCancel={isEditing ? handleClose : undefined}
          loading={stepLoading}
        />
      );
    }

    return (
      <ServiceAddOnsStep
        categoryLabel={ADD_ONS_STEP_LABELS[planCategory]}
        onClose={handleClose}
        onSkip={skipAddOns}
        onAddToPlan={isEditing && onSaveComplete ? onSaveComplete : addSelectedAddOnsToPlan}
        confirmLabel={isEditing ? 'Save Changes' : undefined}
        onCancel={isEditing ? handleClose : undefined}
        loading={stepLoading}
      />
    );
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} variant="configurePlanDialog">
        {renderStep()}
      </Dialog>

      {!isEditing && (
        <LeavePlanSetupDialog open={showLeaveDialog} onLeave={handleLeave} onContinue={dismissLeaveDialog} />
      )}

      {isEditing && (
        <WarningDialog
          open={showDiscardWarning}
          title="You Have Unsaved Changes"
          description="It looks like you're still editing your plan. If you leave now, none of your changes will be saved."
          outlinedButtonText="Leave Without Saving"
          containedButtonText="Continue Editing"
          onOutlinedClick={discardChanges}
          onContainedClick={dismissDiscardWarning}
          onClose={dismissDiscardWarning}
        />
      )}

      <ChannelListSideModal
        open={channelSideModal.open}
        onClose={closeChannelSideModal}
        channels={channelSideModal.channels}
      />
    </>
  );
};
